import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

// molecule — DWR level-up row nella timeline LevelsRail dell'Hub.
// Pattern .level-row mockup (styles.css righe 1703-1753): grid 3-col (badge L#
// + body label+summary + edit-icon).
//
// **FI-46 PR-B**: aggiunto `onClick` opzionale per abilitare la navigazione
// retroattiva all'edit del level-up. Quando `onClick` è presente, la row
// diventa un `<button>` con hover/cursor; quando assente, resta `<div>`
// display-only (preserva comportamento PR 2 per L1 e per Hub stampabile).

interface LevelRowProps {
  level: number;
  /** Label uppercase del livello (es. "L1 · Creazione", "L2"). */
  label: string;
  /** Sommario testuale italic del contenuto del livello. */
  summary?: ReactNode;
  /**
   * Handler click — se presente, la row diventa interattiva (`<button>`
   * con hover/cursor). FI-46 PR-B: rende il livello editabile retroattivamente.
   */
  onClick?: (() => void) | undefined;
  /** Accessibility label per il button (es. "Modifica L3"). */
  ariaLabel?: string | undefined;
  className?: string | undefined;
}

export function LevelRow({ level, label, summary, onClick, ariaLabel, className }: LevelRowProps) {
  const content = <LevelRowContent level={level} label={label} summary={summary} />;
  const baseClass =
    'border-rule grid w-full grid-cols-[auto_minmax(0,1fr)] items-start gap-[0.55rem] border-b border-dotted px-[0.4rem] py-[0.5rem] text-left last:border-b-0';

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel ?? `Modifica ${label}`}
        className={cn(
          baseClass,
          'hover:bg-paper-soft focus-visible:ring-ink cursor-pointer bg-transparent transition focus-visible:ring-2 focus-visible:outline-none',
          className,
        )}
      >
        {content}
      </button>
    );
  }

  return <div className={cn(baseClass, className)}>{content}</div>;
}

function LevelRowContent({
  level,
  label,
  summary,
}: {
  level: number;
  label: string;
  summary?: ReactNode;
}) {
  return (
    <>
      <span className="border-ink bg-ink text-paper-bright font-display inline-grid h-[26px] min-w-[26px] place-items-center border px-1 text-center text-[0.72rem] font-bold tracking-[0.06em]">
        L{level}
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="font-display text-[0.78rem] tracking-[0.06em] uppercase">{label}</span>
        <span className="text-ink-soft overflow-hidden text-[0.78rem] italic text-ellipsis">
          {summary ?? <em className="opacity-70">—</em>}
        </span>
      </span>
    </>
  );
}
